import { Flex, Divider, HStack, Box, Text, DataList, DataItem } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { parseISO } from 'date-fns';
import { useEnabledFeatures } from '../../../../common/hooks/enabled-features/use-enabled-features';
import { useEsriMapStore } from '../../../hooks/use-esri-map-store';
import { DELAY_FOR_UNIT_UPDATE } from '../../../configuration';
import { LastUpdateTime } from '../last-update-time';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { getOneSizeSmaller } from '../../../../common/helpers/utils';
import { getDataItemText } from '../../../../common/helpers/component-utils';

export const UnitInfoSection = observer(function UnitInfoSection(): React.ReactElement {
    const mapStore = useEsriMapStore();
    const { multiAgencySubdivisions, staffCountEnabled } = useEnabledFeatures();

    const selectedMapLedgerUnit = mapStore.selectedMapLedgerUnit;

    const { isFirstResponder } = useApplicationType();
    const size = isFirstResponder ? 'lg' : 'md';
    const smallerSize = getOneSizeSmaller(size);

    const {
        subdivisions,
        dispatchArea,
        unitStatusUpdatedDateUtc,
        equipment,
        skills,
        shiftNotes,
        unitStateDisplay,
        tag,
        unitAgency,
        staffCount,
    } = selectedMapLedgerUnit || {};

    const subdivisionLabel = multiAgencySubdivisions ? `${unitAgency} Subd.` : 'Subdivisions';

    const unitStatusUpdatedDateParsed = parseISO(unitStatusUpdatedDateUtc!);
    const statusValue = unitStateDisplay ? unitStateDisplay : '--';

    return (
        <Box data-testid="MAP_UNIT_INFO_SECTION">
            <Flex direction="column" padding="12px 18px">
                <Box marginBottom={3}>
                    <Text fontSize={size} fontWeight="bold">
                        Unit Info
                    </Text>
                </Box>
                <DataList direction="column" variant="stacked" spacing={3}>
                    <HStack alignItems="flex-start" gap={4}>
                        <DataItem
                            dataKey={<Text fontSize={smallerSize}>{subdivisionLabel}</Text>}
                            style={{ flexBasis: '50%', wordBreak: 'break-word' }}
                        >
                            {getDataItemText(size, subdivisions)}
                        </DataItem>

                        <DataItem
                            dataKey={<Text fontSize={smallerSize}>D-Area</Text>}
                            style={{ flexBasis: '50%', wordBreak: 'break-word' }}
                        >
                            {getDataItemText(size, dispatchArea)}
                        </DataItem>
                    </HStack>
                    {staffCountEnabled && (
                        <DataItem
                            dataKey={<Text fontSize={smallerSize}>Staff Count</Text>}
                            style={{ wordBreak: 'break-word' }}
                        >
                            {getDataItemText(size, staffCount ?? 0)}
                        </DataItem>
                    )}
                    <HStack alignItems="flex-start" gap={4}>
                        <DataItem
                            dataKey={<Text fontSize={smallerSize}>Status</Text>}
                            style={{ flexBasis: '50%', wordBreak: 'break-word' }}
                            data-testid="MAP_UNIT_STATUS_INFO"
                        >
                            <Box>
                                {getDataItemText(size, statusValue)}
                                <LastUpdateTime
                                    timeUpdateKey={statusValue}
                                    lastTimestamp={unitStatusUpdatedDateParsed}
                                    delay={DELAY_FOR_UNIT_UPDATE}
                                    size={size}
                                />
                            </Box>
                        </DataItem>

                        <DataItem
                            dataKey={<Text fontSize={smallerSize}>Vehicle</Text>}
                            style={{ flexBasis: '50%', wordBreak: 'break-word' }}
                        >
                            {getDataItemText(size, tag)}
                        </DataItem>
                    </HStack>
                    <HStack alignItems="flex-start" gap={4}>
                        <DataItem
                            dataKey={<Text fontSize={smallerSize}>Equipment</Text>}
                            style={{ flexBasis: '50%', wordBreak: 'break-word' }}
                        >
                            {getDataItemText(size, equipment)}
                        </DataItem>
                        <DataItem
                            dataKey={<Text fontSize={smallerSize}>Skills</Text>}
                            style={{ flexBasis: '50%', wordBreak: 'break-word' }}
                        >
                            {getDataItemText(size, skills)}
                        </DataItem>
                    </HStack>
                    <DataItem
                        dataKey={<Text fontSize={smallerSize}>Shift Notes</Text>}
                        style={{ wordBreak: 'break-word' }}
                    >
                        {getDataItemText(size, shiftNotes)}
                    </DataItem>
                </DataList>
            </Flex>
            <Divider />
        </Box>
    );
});
